import { useState, useEffect, useMemo } from 'react'
import { X, ChevronDown, ChevronRight, Check } from 'lucide-react'
import { User, CreateUserInput } from '@/services/usersService'
import plansService, { Plan } from '@/services/plansService'
import { usePermissions } from '@/hooks/usePermissions'

interface UserFormModalProps {
  title: string
  user?: User
  onClose: () => void
  onSubmit: (data: any) => Promise<void>
}

// ----------------------------------------------------------------------------
// Opções espelhadas do cadastro do webapp (apps/web/src/components/auth/*).
// Mantidas em sincronia manual — se mudarem lá, atualizar aqui.
// ----------------------------------------------------------------------------
const STATES = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO',
  'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI',
  'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO',
]

const PROFILE_TYPES = [
  { id: 'producer', label: 'Produtor(a) Rural' },
  { id: 'technician', label: 'Técnico(a) / Consultor(a)' },
  { id: 'researcher', label: 'Pesquisador(a)' },
  { id: 'student', label: 'Estudante' },
  { id: 'institution', label: 'Representante de Instituição' },
  { id: 'other', label: 'Outro' },
]

const HOW_KNEW = [
  { id: 'gaas', label: 'GAAS' },
  { id: 'indication', label: 'Indicação' },
  { id: 'event', label: 'Evento' },
  { id: 'social_media', label: 'Redes sociais' },
  { id: 'website', label: 'Site' },
  { id: 'other', label: 'Outro' },
]

const AI_FAMILIARITY = [
  { id: 'never', label: 'Nunca usei' },
  { id: 'occasionally', label: 'Uso ocasionalmente' },
  { id: 'frequently', label: 'Uso com frequência' },
]

const BIOMES = ['Amazônia', 'Cerrado', 'Caatinga', 'Mata Atlântica', 'Pampa', 'Pantanal', 'Outro']

const PRODUCTION_SYSTEMS = ['Convencional', 'Em transição', 'Agroecológico', 'Orgânico', 'Regenerativo', 'Outro']

const ACTIVITIES = [
  'Grãos', 'Cana', 'Café', 'Frutas', 'Hortaliças', 'Floresta',
  'Gado de corte', 'Gado de leite', 'Aves de postura', 'Aves de corte', 'Suínos', 'Outro',
]

const PRACTICES = [
  'Plantio direto', 'Rotação de culturas', 'Cobertura permanente do solo',
  'Adubação verde', 'Bioinsumos', 'Compostagem', 'Integração lavoura-pecuária',
  'Integração lavoura-pecuária-floresta', 'Pastejo rotacionado', 'Irrigação', 'Outro',
]

const CHALLENGES = [
  'Solo', 'Água', 'Pragas', 'Doenças', 'Plantas daninhas', 'Clima',
  'Custo de produção', 'Mão de obra', 'Mercado', 'Outro',
]

const MAIN_OBJECTIVES = [
  { id: 'doubts', label: 'Tirar dúvidas' },
  { id: 'recommendations', label: 'Receber recomendações' },
  { id: 'compare', label: 'Comparar com casos semelhantes' },
  { id: 'register', label: 'Registrar experiências' },
  { id: 'follow', label: 'Acompanhar a transição produtiva' },
  { id: 'learn', label: 'Aprender com ciência aplicada' },
  { id: 'other', label: 'Outro' },
]

const WORK_REGIONS = ['Norte', 'Nordeste', 'Centro-Oeste', 'Sudeste', 'Sul', 'Nacional']

const WORK_TYPES = [
  'Autônomo(a)', 'Empresa', 'Cooperativa', 'Assistência técnica',
  'Instituição pública', 'Pesquisa', 'Ensino', 'Outro',
]

const WORK_FIELDS = [
  'Empresa', 'Instituição de ensino', 'Instituição de pesquisa',
  'Poder público', 'Terceiro setor', 'Outro',
]

const MAIN_INTERESTS = [
  { id: 'learn', label: 'Aprender' },
  { id: 'research', label: 'Pesquisar' },
  { id: 'support', label: 'Apoiar produtores' },
  { id: 'develop', label: 'Desenvolver projetos' },
  { id: 'follow', label: 'Acompanhar casos' },
  { id: 'other', label: 'Outro' },
]

const PREFERRED_TOPICS = [
  'Solo', 'Clima', 'Bioinsumos', 'Nutrição de plantas', 'Manejo de pragas',
  'Pecuária', 'Transição regenerativa', 'Planejamento produtivo', 'Mercado', 'Outro',
]

const YES_NO_TRANSITION = [
  { id: 'yes', label: 'Sim' },
  { id: 'no', label: 'Não' },
  { id: 'transition', label: 'Em fase de transição' },
]

const GAAS_ASSOCIATED = [
  { id: 'yes', label: 'Sim' },
  { id: 'no', label: 'Não' },
  { id: 'dont_know', label: 'Ainda não sei' },
]

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 text-sm'

const labelClass = 'block text-sm font-medium text-gray-700 mb-1'

function formatCPF(value: string) {
  const digits = value.replace(/\D/g, '').slice(0, 11)
  return digits
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2')
}

/** Validação algorítmica de CPF — mesma regra do backend (utils/validators.ts). */
function isValidCPF(cpf: string) {
  const clean = cpf.replace(/\D/g, '')
  if (clean.length !== 11) return false
  if (/^(\d)\1{10}$/.test(clean)) return false

  let sum = 0
  for (let i = 0; i < 9; i++) sum += parseInt(clean[i]) * (10 - i)
  let check = (sum * 10) % 11
  if (check === 10) check = 0
  if (check !== parseInt(clean[9])) return false

  sum = 0
  for (let i = 0; i < 10; i++) sum += parseInt(clean[i]) * (11 - i)
  check = (sum * 10) % 11
  if (check === 10) check = 0
  return check === parseInt(clean[10])
}

interface SectionProps {
  title: string
  subtitle?: string
  open: boolean
  onToggle: () => void
  children: React.ReactNode
}

function Section({ title, subtitle, open, onToggle, children }: SectionProps) {
  return (
    <div className="border border-gray-200 rounded-lg">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 rounded-lg"
      >
        <div>
          <span className="text-sm font-semibold text-gray-900">{title}</span>
          {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
        {open ? (
          <ChevronDown className="h-4 w-4 text-gray-400 shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
        )}
      </button>
      {open && <div className="px-4 pb-4 pt-1">{children}</div>}
    </div>
  )
}

interface ChipsProps {
  options: string[]
  selected: string[]
  onChange: (values: string[]) => void
}

function Chips({ options, selected, onChange }: ChipsProps) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((option) => {
        const active = selected.includes(option)
        return (
          <button
            key={option}
            type="button"
            onClick={() =>
              onChange(active ? selected.filter((o) => o !== option) : [...selected, option])
            }
            className={`px-3 py-1.5 rounded-full border text-xs transition-colors ${
              active
                ? 'border-blue-500 bg-blue-50 text-blue-700'
                : 'border-gray-300 text-gray-600 hover:border-gray-400'
            }`}
          >
            {option}
          </button>
        )
      })}
    </div>
  )
}

type FormState = Record<string, any>

const EMPTY_FORM: FormState = {
  name: '',
  email: '',
  password: '',
  role: 'user',
  status: 'active',
  planId: '',
  isTestUser: true,

  profileType: '',
  cpf: '',
  username: '',
  phone: '',
  city: '',
  state: '',
  profession: '',
  howKnew: '',
  aiFamiliarity: '',

  propertySizeHa: '',
  biome: '',
  soilType: '',
  propertyCity: '',
  propertyState: '',
  productionSystem: '',
  gaasAssociated: '',
  activities: [],
  practices: [],
  challenges: [],
  mainObjective: '',

  professionalFormation: '',
  workArea: '',
  workRegion: '',
  workType: '',
  regenerativeExperience: '',
  experienceYears: '',

  institution: '',
  workField: '',
  mainInterest: '',
  preferredTopics: [],

  observations: '',
  dataSharingConsent: false,
  termsAccepted: false,
}

export function UserFormModal({ title, user, onClose, onSubmit }: UserFormModalProps) {
  const { isGod } = usePermissions()
  const isEdit = !!user

  const [formData, setFormData] = useState<FormState>({
    ...EMPTY_FORM,
    name: user?.name || '',
    email: user?.email || '',
    role: user?.role || 'user',
    status: user?.status || 'active',
  })
  const [plans, setPlans] = useState<Plan[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [openSection, setOpenSection] = useState<string | null>('profile')

  useEffect(() => {
    if (!isEdit) {
      plansService.listPlans().then(setPlans).catch(() => { })
    }
  }, [isEdit])

  const set = (patch: FormState) => setFormData((prev) => ({ ...prev, ...patch }))

  const passwordChecks = useMemo(() => {
    const p = formData.password as string
    return [
      { label: 'Mínimo 8 caracteres', ok: p.length >= 8 },
      { label: 'Letra maiúscula', ok: /[A-Z]/.test(p) },
      { label: 'Letra minúscula', ok: /[a-z]/.test(p) },
      { label: 'Número', ok: /\d/.test(p) },
      { label: 'Caractere especial', ok: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(p) },
    ]
  }, [formData.password])

  const passwordValid = passwordChecks.every((c) => c.ok)

  const cpfError = useMemo(() => {
    const cpf = formData.cpf as string
    if (!cpf) return ''
    return isValidCPF(cpf) ? '' : 'CPF inválido'
  }, [formData.cpf])

  const usernameError = useMemo(() => {
    const username = formData.username as string
    if (!username) return ''
    if (username.length < 3 || username.length > 20) return '3 a 20 caracteres'
    if (!/^[a-zA-Z0-9_]+$/.test(username)) return 'Apenas letras, números e underscore'
    return ''
  }, [formData.username])

  /**
   * Monta o payload enviado ao backend descartando campos vazios — o schema
   * do servidor valida tamanho fixo em CPF e UF, então string vazia seria
   * rejeitada em vez de tratada como ausente.
   */
  const buildCreatePayload = (): CreateUserInput => {
    const payload: Record<string, any> = {
      email: formData.email,
      password: formData.password,
      name: formData.name,
      role: formData.role,
    }

    const textFields = [
      'username', 'cpf', 'phone', 'city', 'state', 'profession',
      'profileType', 'howKnew', 'aiFamiliarity',
      'biome', 'soilType', 'propertyCity', 'propertyState', 'productionSystem',
      'gaasAssociated', 'mainObjective', 'professionalFormation', 'workArea',
      'workRegion', 'workType', 'regenerativeExperience', 'experienceYears',
      'institution', 'workField', 'mainInterest', 'observations',
    ]

    for (const field of textFields) {
      const value = (formData[field] as string)?.trim()
      if (value) payload[field] = value
    }

    for (const field of ['activities', 'practices', 'challenges', 'preferredTopics']) {
      const value = formData[field] as string[]
      if (value?.length) payload[field] = value
    }

    if (formData.propertySizeHa) {
      const parsed = parseFloat(formData.propertySizeHa)
      if (!Number.isNaN(parsed)) payload.propertySizeHa = parsed
    }

    if (formData.planId) payload.planId = formData.planId
    if (formData.isTestUser) payload.isTestUser = true
    if (formData.dataSharingConsent) payload.dataSharingConsent = true
    if (formData.termsAccepted) payload.termsAccepted = true

    return payload as CreateUserInput
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!isEdit) {
      if (!passwordValid) {
        setError('A senha não atende aos requisitos mínimos')
        return
      }
      if (cpfError || usernameError) {
        setError('Corrija os campos destacados antes de continuar')
        return
      }
    }

    setLoading(true)
    setError('')

    try {
      if (isEdit) {
        // Edição — não envia email nem senha
        await onSubmit({
          name: formData.name,
          role: formData.role,
          status: formData.status,
        })
      } else {
        await onSubmit(buildCreatePayload())
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao salvar usuário')
      setLoading(false)
    }
  }

  const toggleSection = (key: string) => setOpenSection((prev) => (prev === key ? null : key))

  const profileType = formData.profileType as string
  const showProducer = profileType === 'producer'
  const showTechnician = profileType === 'technician'
  const showResearcher = ['researcher', 'student', 'institution'].includes(profileType)
  const hasQuestionnaire = showProducer || showTechnician || showResearcher

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`bg-white rounded-lg w-full flex flex-col max-h-[90vh] ${isEdit ? 'max-w-md' : 'max-w-3xl'}`}>
        <div className="flex justify-between items-center px-6 py-4 border-b">
          <h2 className="text-xl font-bold">{title}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="overflow-y-auto px-6 py-4 space-y-4">
            {error && (
              <div className="p-3 bg-red-100 text-red-700 rounded-md text-sm">
                {error}
              </div>
            )}

            {/* ---------------------------------------------------------- */}
            {/* Conta                                                       */}
            {/* ---------------------------------------------------------- */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className={isEdit ? 'md:col-span-2' : ''}>
                <label className={labelClass}>Nome *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => set({ name: e.target.value })}
                  className={inputClass}
                />
              </div>

              {!isEdit && (
                <>
                  <div>
                    <label className={labelClass}>Email *</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => set({ email: e.target.value })}
                      className={inputClass}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className={labelClass}>Senha *</label>
                    <input
                      type="password"
                      required
                      value={formData.password}
                      onChange={(e) => set({ password: e.target.value })}
                      className={inputClass}
                    />
                    {formData.password && (
                      <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2">
                        {passwordChecks.map((check) => (
                          <span
                            key={check.label}
                            className={`text-xs flex items-center gap-1 ${check.ok ? 'text-green-600' : 'text-gray-400'}`}
                          >
                            {check.ok ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                            {check.label}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}

              <div>
                <label className={labelClass}>Role</label>
                <select
                  value={formData.role}
                  onChange={(e) => set({ role: e.target.value })}
                  className={inputClass}
                >
                  <option value="user">User</option>
                  <option value="creator">Creator</option>
                  <option value="partner">Partner</option>
                  <option value="admin">Admin</option>
                  {isGod() && <option value="god">God</option>}
                </select>
              </div>

              {isEdit && (
                <div>
                  <label className={labelClass}>Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => set({ status: e.target.value })}
                    className={inputClass}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>
              )}

              {!isEdit && (
                <div>
                  <label className={labelClass}>Plano</label>
                  <select
                    value={formData.planId}
                    onChange={(e) => set({ planId: e.target.value })}
                    className={inputClass}
                  >
                    <option value="">Sem plano (uso ilimitado)</option>
                    {plans.filter((p) => p.isActive).map((p) => (
                      <option key={p.id} value={p.id}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {!isEdit && (
              <label className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isTestUser}
                  onChange={(e) => set({ isTestUser: e.target.checked })}
                  className="mt-0.5"
                />
                <span className="text-sm text-amber-900">
                  Usuário de teste
                  <span className="block text-xs text-amber-700">
                    Marca a conta para diferenciá-la dos cadastros reais na listagem.
                  </span>
                </span>
              </label>
            )}

            {/* ---------------------------------------------------------- */}
            {/* Perfil                                                      */}
            {/* ---------------------------------------------------------- */}
            {!isEdit && (
              <>
                <Section
                  title="Dados de perfil"
                  subtitle="Todos opcionais — CPF pode ficar em branco em contas de teste"
                  open={openSection === 'profile'}
                  onToggle={() => toggleSection('profile')}
                >
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className={labelClass}>Tipo de perfil</label>
                      <select
                        value={formData.profileType}
                        onChange={(e) => set({ profileType: e.target.value })}
                        className={inputClass}
                      >
                        <option value="">Selecione...</option>
                        {PROFILE_TYPES.map((t) => (
                          <option key={t.id} value={t.id}>{t.label}</option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        Define quais campos aparecem na seção do questionário.
                      </p>
                    </div>

                    <div>
                      <label className={labelClass}>CPF</label>
                      <input
                        type="text"
                        value={formData.cpf}
                        onChange={(e) => set({ cpf: formatCPF(e.target.value) })}
                        placeholder="000.000.000-00"
                        className={`${inputClass} ${cpfError ? 'border-red-500' : ''}`}
                      />
                      {cpfError ? (
                        <p className="text-xs text-red-600 mt-1">{cpfError}</p>
                      ) : (
                        <p className="text-xs text-gray-500 mt-1">
                          Deixe vazio em contas de teste — um CPF real aqui bloqueia o cadastro
                          do titular no app.
                        </p>
                      )}
                    </div>

                    <div>
                      <label className={labelClass}>Username</label>
                      <input
                        type="text"
                        value={formData.username}
                        onChange={(e) => set({ username: e.target.value })}
                        placeholder="usuario_unico"
                        maxLength={20}
                        className={`${inputClass} ${usernameError ? 'border-red-500' : ''}`}
                      />
                      {usernameError ? (
                        <p className="text-xs text-red-600 mt-1">{usernameError}</p>
                      ) : (
                        <p className="text-xs text-gray-500 mt-1">
                          Se vazio, é gerado automaticamente a partir do email.
                        </p>
                      )}
                    </div>

                    <div>
                      <label className={labelClass}>Telefone / WhatsApp</label>
                      <input
                        type="text"
                        value={formData.phone}
                        onChange={(e) => set({ phone: e.target.value })}
                        placeholder="(00) 00000-0000"
                        maxLength={20}
                        className={inputClass}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Profissão / Área de atuação</label>
                      <input
                        type="text"
                        value={formData.profession}
                        onChange={(e) => set({ profession: e.target.value })}
                        className={inputClass}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Cidade</label>
                      <input
                        type="text"
                        value={formData.city}
                        onChange={(e) => set({ city: e.target.value })}
                        className={inputClass}
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Estado (UF)</label>
                      <select
                        value={formData.state}
                        onChange={(e) => set({ state: e.target.value })}
                        className={inputClass}
                      >
                        <option value="">Selecione...</option>
                        {STATES.map((uf) => (
                          <option key={uf} value={uf}>{uf}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Como conheceu o RegenerAI</label>
                      <select
                        value={formData.howKnew}
                        onChange={(e) => set({ howKnew: e.target.value })}
                        className={inputClass}
                      >
                        <option value="">Selecione...</option>
                        {HOW_KNEW.map((o) => (
                          <option key={o.id} value={o.id}>{o.label}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Familiaridade com IA</label>
                      <select
                        value={formData.aiFamiliarity}
                        onChange={(e) => set({ aiFamiliarity: e.target.value })}
                        className={inputClass}
                      >
                        <option value="">Selecione...</option>
                        {AI_FAMILIARITY.map((o) => (
                          <option key={o.id} value={o.id}>{o.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </Section>

                {/* ------------------------------------------------------ */}
                {/* Questionário (condicional ao tipo de perfil)            */}
                {/* ------------------------------------------------------ */}
                <Section
                  title="Questionário"
                  subtitle={
                    hasQuestionnaire
                      ? 'Campos específicos do tipo de perfil selecionado'
                      : 'Selecione um tipo de perfil para exibir os campos'
                  }
                  open={openSection === 'questionnaire'}
                  onToggle={() => toggleSection('questionnaire')}
                >
                  {!hasQuestionnaire ? (
                    <p className="text-sm text-gray-500 py-2">
                      Nenhum campo específico para este tipo de perfil.
                    </p>
                  ) : (
                    <div className="space-y-4">
                      {showProducer && (
                        <>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className={labelClass}>Tamanho da área (ha)</label>
                              <input
                                type="number"
                                step="0.01"
                                value={formData.propertySizeHa}
                                onChange={(e) => set({ propertySizeHa: e.target.value })}
                                className={inputClass}
                              />
                            </div>

                            <div>
                              <label className={labelClass}>Bioma</label>
                              <select
                                value={formData.biome}
                                onChange={(e) => set({ biome: e.target.value })}
                                className={inputClass}
                              >
                                <option value="">Selecione...</option>
                                {BIOMES.map((b) => <option key={b} value={b}>{b}</option>)}
                              </select>
                            </div>

                            <div>
                              <label className={labelClass}>Tipo de solo predominante</label>
                              <input
                                type="text"
                                value={formData.soilType}
                                onChange={(e) => set({ soilType: e.target.value })}
                                className={inputClass}
                              />
                            </div>

                            <div>
                              <label className={labelClass}>Sistema de produção</label>
                              <select
                                value={formData.productionSystem}
                                onChange={(e) => set({ productionSystem: e.target.value })}
                                className={inputClass}
                              >
                                <option value="">Selecione...</option>
                                {PRODUCTION_SYSTEMS.map((s) => <option key={s} value={s}>{s}</option>)}
                              </select>
                            </div>

                            <div>
                              <label className={labelClass}>Cidade da propriedade</label>
                              <input
                                type="text"
                                value={formData.propertyCity}
                                onChange={(e) => set({ propertyCity: e.target.value })}
                                className={inputClass}
                              />
                            </div>

                            <div>
                              <label className={labelClass}>UF da propriedade</label>
                              <select
                                value={formData.propertyState}
                                onChange={(e) => set({ propertyState: e.target.value })}
                                className={inputClass}
                              >
                                <option value="">Selecione...</option>
                                {STATES.map((uf) => <option key={uf} value={uf}>{uf}</option>)}
                              </select>
                            </div>

                            <div>
                              <label className={labelClass}>É associado ao GAAS?</label>
                              <select
                                value={formData.gaasAssociated}
                                onChange={(e) => set({ gaasAssociated: e.target.value })}
                                className={inputClass}
                              >
                                <option value="">Selecione...</option>
                                {GAAS_ASSOCIATED.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                              </select>
                            </div>

                            <div>
                              <label className={labelClass}>Principal objetivo</label>
                              <select
                                value={formData.mainObjective}
                                onChange={(e) => set({ mainObjective: e.target.value })}
                                className={inputClass}
                              >
                                <option value="">Selecione...</option>
                                {MAIN_OBJECTIVES.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                              </select>
                            </div>
                          </div>

                          <div>
                            <label className={labelClass}>O que cultiva ou cria?</label>
                            <Chips
                              options={ACTIVITIES}
                              selected={formData.activities}
                              onChange={(v) => set({ activities: v })}
                            />
                          </div>

                          <div>
                            <label className={labelClass}>Práticas de manejo</label>
                            <Chips
                              options={PRACTICES}
                              selected={formData.practices}
                              onChange={(v) => set({ practices: v })}
                            />
                          </div>

                          <div>
                            <label className={labelClass}>Principais desafios</label>
                            <Chips
                              options={CHALLENGES}
                              selected={formData.challenges}
                              onChange={(v) => set({ challenges: v })}
                            />
                          </div>
                        </>
                      )}

                      {showTechnician && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className={labelClass}>Formação profissional</label>
                            <input
                              type="text"
                              value={formData.professionalFormation}
                              onChange={(e) => set({ professionalFormation: e.target.value })}
                              className={inputClass}
                            />
                          </div>

                          <div>
                            <label className={labelClass}>Área de atuação</label>
                            <input
                              type="text"
                              value={formData.workArea}
                              onChange={(e) => set({ workArea: e.target.value })}
                              className={inputClass}
                            />
                          </div>

                          <div>
                            <label className={labelClass}>Região de atuação</label>
                            <select
                              value={formData.workRegion}
                              onChange={(e) => set({ workRegion: e.target.value })}
                              className={inputClass}
                            >
                              <option value="">Selecione...</option>
                              {WORK_REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                            </select>
                          </div>

                          <div>
                            <label className={labelClass}>Tipo de vínculo</label>
                            <select
                              value={formData.workType}
                              onChange={(e) => set({ workType: e.target.value })}
                              className={inputClass}
                            >
                              <option value="">Selecione...</option>
                              {WORK_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                          </div>

                          <div>
                            <label className={labelClass}>Atua com agricultura regenerativa?</label>
                            <select
                              value={formData.regenerativeExperience}
                              onChange={(e) => set({ regenerativeExperience: e.target.value })}
                              className={inputClass}
                            >
                              <option value="">Selecione...</option>
                              {YES_NO_TRANSITION.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                            </select>
                          </div>

                          <div>
                            <label className={labelClass}>Tempo de atuação</label>
                            <input
                              type="text"
                              value={formData.experienceYears}
                              onChange={(e) => set({ experienceYears: e.target.value })}
                              placeholder="Ex: 5 anos"
                              className={inputClass}
                            />
                          </div>
                        </div>
                      )}

                      {showResearcher && (
                        <>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className={labelClass}>Instituição / Organização</label>
                              <input
                                type="text"
                                value={formData.institution}
                                onChange={(e) => set({ institution: e.target.value })}
                                className={inputClass}
                              />
                            </div>

                            <div>
                              <label className={labelClass}>Área de formação</label>
                              <input
                                type="text"
                                value={formData.professionalFormation}
                                onChange={(e) => set({ professionalFormation: e.target.value })}
                                className={inputClass}
                              />
                            </div>

                            <div>
                              <label className={labelClass}>Tipo de atuação</label>
                              <select
                                value={formData.workField}
                                onChange={(e) => set({ workField: e.target.value })}
                                className={inputClass}
                              >
                                <option value="">Selecione...</option>
                                {WORK_FIELDS.map((f) => <option key={f} value={f}>{f}</option>)}
                              </select>
                            </div>

                            <div>
                              <label className={labelClass}>Interesse no RegenerAI</label>
                              <select
                                value={formData.mainInterest}
                                onChange={(e) => set({ mainInterest: e.target.value })}
                                className={inputClass}
                              >
                                <option value="">Selecione...</option>
                                {MAIN_INTERESTS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                              </select>
                            </div>
                          </div>

                          <div>
                            <label className={labelClass}>Temas de maior interesse</label>
                            <Chips
                              options={PREFERRED_TOPICS}
                              selected={formData.preferredTopics}
                              onChange={(v) => set({ preferredTopics: v })}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </Section>

                {/* ------------------------------------------------------ */}
                {/* Observações e consentimentos                            */}
                {/* ------------------------------------------------------ */}
                <Section
                  title="Observações e consentimentos"
                  open={openSection === 'extra'}
                  onToggle={() => toggleSection('extra')}
                >
                  <div className="space-y-4">
                    <div>
                      <label className={labelClass}>Observações</label>
                      <textarea
                        rows={3}
                        maxLength={1000}
                        value={formData.observations}
                        onChange={(e) => set({ observations: e.target.value })}
                        className={inputClass}
                      />
                    </div>

                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.dataSharingConsent}
                        onChange={(e) => set({ dataSharingConsent: e.target.checked })}
                        className="mt-0.5"
                      />
                      <span className="text-sm text-gray-700">
                        Consentimento de compartilhamento de dados
                      </span>
                    </label>

                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.termsAccepted}
                        onChange={(e) => set({ termsAccepted: e.target.checked })}
                        className="mt-0.5"
                      />
                      <span className="text-sm text-gray-700">
                        Registrar aceite dos termos de uso
                        <span className="block text-xs text-gray-500">
                          Grava data, versão 1.0 e a origem como criação via admin. Deixe
                          desmarcado se não houve aceite real.
                        </span>
                      </span>
                    </label>
                  </div>
                </Section>
              </>
            )}
          </div>

          <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Salvando...' : isEdit ? 'Salvar' : 'Criar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
